import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line,
} from 'recharts';
import { TrendingUp, Users, Shield, BarChart3, RefreshCw } from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';

const COLORS = {
  navy: '#0F1B2D',
  gold: '#C4A35A',
  stone: '#9B9589',
  cream: '#EDECEA',
  warm: '#4A4640',
};

const PIE_COLORS = ['#C4A35A', '#0F1B2D', '#dc2626'];
const BAR_COLORS = ['#0F1B2D', '#C4A35A', '#6366f1', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4', '#e11d48', '#84cc16'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border-2 border-[#0F1B2D] px-4 py-3 shadow-lg">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-semibold" style={{ color: p.color || COLORS.navy }}>
          {p.name}: <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent = false }) => (
  <div className={`border-2 ${accent ? 'border-[#C4A35A] bg-[#C4A35A]/5' : 'border-[#D8D3CB] bg-white'} p-5`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-8 h-8 flex items-center justify-center ${accent ? 'bg-[#C4A35A]/20' : 'bg-[#0F1B2D]/5'}`}>
        <Icon size={14} className={accent ? 'text-[#C4A35A]' : 'text-[#0F1B2D]'} />
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589]">{label}</span>
    </div>
    <p className="font-['Playfair_Display'] text-2xl font-bold text-[#0F1B2D]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
      {value}
    </p>
    {sub && <p className="text-[10px] text-[#9B9589] mt-1 font-medium">{sub}</p>}
  </div>
);

const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { data: res } = await apiClient.get('/admin/analytics');
      setData(res);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <RefreshCw size={20} className="animate-spin text-[#C4A35A]" />
        <span className="ml-3 text-sm text-[#9B9589] font-medium">Loading analytics...</span>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-16 text-[#9B9589]">No analytics data available</div>;
  }

  const approvalRate = data.total_apps > 0
    ? Math.round((data.app_status.find(s => s.name === 'Approved')?.value || 0) / data.total_apps * 100)
    : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8" data-testid="analytics-panel">

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Members" value={data.total_members} sub={`${data.trust_score.total_scored} scored`} />
        <StatCard icon={BarChart3} label="Total Applications" value={data.total_apps} sub={`${approvalRate}% approval rate`} />
        <StatCard icon={Shield} label="Avg Trust Score" value={data.trust_score.avg} sub={`Range: ${data.trust_score.min} – ${data.trust_score.max}`} accent />
        <StatCard icon={TrendingUp} label="Scored Members" value={data.trust_score.total_scored} sub={`of ${data.total_members} total`} />
      </div>

      {/* Row 1: Approval Pie + Trust Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Application Status Pie */}
        <div className="border-2 border-[#D8D3CB] bg-white p-6" data-testid="chart-app-status">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B9589] mb-6">Application Status Breakdown</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.app_status}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {data.app_status.map((entry, i) => (
                    <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  iconType="square"
                  iconSize={8}
                  formatter={(val) => <span className="text-[10px] font-bold uppercase tracking-wider text-[#4A4640]">{val}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 text-center">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589]">Approval Rate: </span>
            <span className="font-bold text-lg text-[#0F1B2D]" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="approval-rate">{approvalRate}%</span>
          </div>
        </div>

        {/* Trust Score Distribution */}
        <div className="border-2 border-[#D8D3CB] bg-white p-6" data-testid="chart-trust-dist">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B9589] mb-6">Trust Score Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.trust_score.distribution} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" vertical={false} />
                <XAxis dataKey="range" tick={{ fontSize: 10, fontWeight: 700, fill: COLORS.stone }} axisLine={{ stroke: '#D8D3CB' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: COLORS.stone }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill={COLORS.gold} radius={[3, 3, 0, 0]} name="Members" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-between mt-4 px-2">
            <div className="text-center">
              <p className="text-[9px] font-bold uppercase text-[#9B9589]">Min</p>
              <p className="text-sm font-bold text-[#0F1B2D]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{data.trust_score.min}</p>
            </div>
            <div className="text-center">
              <p className="text-[9px] font-bold uppercase text-[#C4A35A]">Average</p>
              <p className="text-sm font-bold text-[#C4A35A]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{data.trust_score.avg}</p>
            </div>
            <div className="text-center">
              <p className="text-[9px] font-bold uppercase text-[#9B9589]">Max</p>
              <p className="text-sm font-bold text-[#0F1B2D]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{data.trust_score.max}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Application Timeline + Member Growth */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Application Timeline */}
        <div className="border-2 border-[#D8D3CB] bg-white p-6" data-testid="chart-app-timeline">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B9589] mb-6">Application Trends (30 Days)</h3>
          <div className="h-56">
            {data.app_timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.app_timeline}>
                  <defs>
                    <linearGradient id="gradApps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.gold} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.gold} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: COLORS.stone }} axisLine={{ stroke: '#D8D3CB' }} tickLine={false}
                    tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
                  <YAxis tick={{ fontSize: 10, fill: COLORS.stone }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total" stroke={COLORS.gold} fill="url(#gradApps)" strokeWidth={2} name="Applications" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-[#9B9589] text-xs">No data in the last 30 days</div>
            )}
          </div>
        </div>

        {/* Member Growth */}
        <div className="border-2 border-[#D8D3CB] bg-white p-6" data-testid="chart-member-growth">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B9589] mb-6">Member Growth</h3>
          <div className="h-56">
            {data.member_timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.member_timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: COLORS.stone }} axisLine={{ stroke: '#D8D3CB' }} tickLine={false}
                    tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
                  <YAxis tick={{ fontSize: 10, fill: COLORS.stone }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="total" stroke={COLORS.navy} strokeWidth={2} dot={false} name="Cumulative" />
                  <Line type="monotone" dataKey="new" stroke={COLORS.gold} strokeWidth={2} dot={{ fill: COLORS.gold, r: 3 }} name="New" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-[#9B9589] text-xs">No member data</div>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Professions + Fields */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Professions Bar Chart */}
        <div className="border-2 border-[#D8D3CB] bg-white p-6" data-testid="chart-professions">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B9589] mb-6">Members by Profession</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.professions} layout="vertical" barSize={16} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: COLORS.stone }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontWeight: 600, fill: COLORS.warm }} axisLine={false} tickLine={false} width={110} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Members" radius={[0, 3, 3, 0]}>
                  {data.professions.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Fields Breakdown */}
        <div className="border-2 border-[#D8D3CB] bg-white p-6" data-testid="chart-fields">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9B9589] mb-6">Members by Creative Field</h3>
          <div className="h-72">
            {data.fields.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.fields} layout="vertical" barSize={16} margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DE" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: COLORS.stone }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontWeight: 600, fill: COLORS.warm }} axisLine={false} tickLine={false} width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill={COLORS.gold} name="Members" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-[#9B9589] text-xs">No field data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Refresh */}
      <div className="text-center pt-2">
        <button onClick={fetchAnalytics} className="inline-flex items-center gap-2 px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] border border-[#D8D3CB] hover:border-[#C4A35A] hover:text-[#C4A35A] transition-colors" data-testid="refresh-analytics">
          <RefreshCw size={12} />Refresh Analytics
        </button>
      </div>
    </motion.div>
  );
};

export default AnalyticsPanel;
